import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, X } from 'lucide-react'
import { fetchDocumentTypes, DocumentTypeCatalog } from '../../lib/api/api'

type Props = {
  /** The selected document type code, or null when none is picked. */
  value: string | null
  onChange: (code: string | null) => void
  disabled?: boolean
}

/**
 * Picks one of the firm's document types (client contracts, statutes,
 * bank forms, …), grouped by category and searchable by name or code.
 * Clicking the already-selected type clears the selection (the server
 * classifies the document itself when none is chosen).
 */
export default function DocumentTypeSelector({ value, onChange, disabled = false }: Props) {
  const { t } = useTranslation()
  const [catalog, setCatalog] = useState<DocumentTypeCatalog | null>(null)
  const [search, setSearch] = useState('')

  useEffect(() => {
    let active = true
    fetchDocumentTypes()
      .then((res) => { if (active) setCatalog(res.data) })
      .catch(() => { if (active) setCatalog({ categories: [] }) })
    return () => { active = false }
  }, [])

  const q = search.trim().toLowerCase()
  const categories = useMemo(() => {
    if (!catalog) return []
    if (!q) return catalog.categories
    return catalog.categories
      .map((category) => ({
        ...category,
        types: category.types.filter(
          (type) => type.name.toLowerCase().includes(q) || type.code.toLowerCase().includes(q)
        ),
      }))
      .filter((category) => category.types.length > 0)
  }, [catalog, q])

  if (!catalog) {
    return <div className="text-sm text-gray-500">{t('common.loading')}</div>
  }

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
      <div className="p-2 border-b border-gray-200 dark:border-gray-700 relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={disabled}
          placeholder={t('documents.typeSelector.searchPlaceholder')}
          aria-label={t('documents.typeSelector.searchPlaceholder')}
          className="w-full pl-9 pr-8 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            aria-label={t('common.clearSearch')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={14} />
          </button>
        )}
      </div>
      <div className="max-h-64 overflow-y-auto">
        {categories.length === 0 ? (
          <div className="px-3 py-2 text-sm text-gray-500">{t('common.noResults')}</div>
        ) : (
          categories.map((category) => (
            <section key={category.slug}>
              <div className="px-3 py-1 text-xs font-semibold text-gray-500 bg-gray-50 dark:bg-gray-900 sticky top-0">
                {category.label}
              </div>
              {category.types.map((type) => {
                const selected = value === type.code
                return (
                  <button
                    key={type.code}
                    type="button"
                    disabled={disabled}
                    onClick={() => onChange(selected ? null : type.code)}
                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-primary-50 disabled:opacity-50 ${
                      selected
                        ? 'bg-primary-100 text-primary-900 font-medium'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <span className="text-gray-500 mr-2">{type.code}</span>
                    {type.name}
                  </button>
                )
              })}
            </section>
          ))
        )}
      </div>
    </div>
  )
}
